import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/ui-system";
import { statusLabel, normalizeStatus } from "@/lib/status-maps";
import { formatToIST } from "@/utils/dateUtils";
import {
  TruckIcon, PackageSearch, Search, Loader2, Clock, PackageX, CheckCircle2,
  ShieldCheck, Boxes, PackageCheck, LogIn, MapPin, ExternalLink, Sparkles, ArrowRight,
} from "lucide-react";

const EDGISTIFY_URL = "https://www.edgistify.com/";
const openEdgistify = () => window.open(EDGISTIFY_URL, "_blank", "noopener,noreferrer");

// Real, publicly-advertised Edgistify metrics (edgistify.com).
const EDGISTIFY_STATS = [
  { value: "1 Lac+", label: "orders / day" },
  { value: "27,000+", label: "pin codes" },
  { value: "50+", label: "cities" },
  { value: "100+", label: "warehouses" },
];

// Two lanes, kept deliberately separate (one public endpoint each). `kind` picks
// the right status-color map (B2B = the 15 ORDER_STATUS, B2C = B2C_ORDER_STATUS).
const LANES = {
  b2c: {
    key: "b2c", kind: "b2c", icon: PackageCheck, path: "track/b2c", param: "awb",
    title: "B2C Last Mile",
    inputLabel: "AWB / Tracking number",
    placeholder: "e.g. 1491234567890",
    help: "Enter the AWB (tracking) number from your shipping or delivery confirmation.",
  },
  b2b: {
    key: "b2b", kind: "order", icon: Boxes, path: "track/b2b", param: "number",
    title: "B2B Freight",
    inputLabel: "LR / Docket / Waybill number",
    placeholder: "e.g. 38123456789",
    help: "Enter the LR / docket / waybill number printed on your freight documents.",
  },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return formatToIST(iso); } catch { return iso; }
};

// Newest scan first; entries without a timestamp sink to the bottom.
const sortCheckpointsDesc = (cps = []) =>
  [...cps].sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : -Infinity;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : -Infinity;
    return tb - ta;
  });

// ---- Macro progress (buyer-friendly 5-stage view of the detailed status) ----
const STAGES = [
  { label: "Booked", icon: PackageCheck },
  { label: "Picked up", icon: Boxes },
  { label: "In transit", icon: TruckIcon },
  { label: "Out for delivery", icon: MapPin },
  { label: "Delivered", icon: CheckCircle2 },
];
const HAPPY = {
  NEW: 0, AWB_ASSIGNED: 0, PICKUP_SCHEDULED: 0, CREATED: 0, MANIFESTED: 0,
  PICKED_UP: 1,
  IN_TRANSIT: 2, LEFT_ORIGIN: 2, REACH_DESTINATION: 2,
  OUT_FOR_DELIVERY: 3, OFD: 3,
  DELIVERED: 4, PART_DEL: 4,
};
const NOTES = {
  NDR: ["warn", "A delivery attempt was unsuccessful — a re-attempt is being arranged."],
  UNDEL_REATTEMPT: ["warn", "A delivery attempt was unsuccessful — a re-attempt is being arranged."],
  NOT_PICKED: ["warn", "Pickup could not be completed yet."],
  CANCELLED: ["bad", "This shipment was cancelled."],
  LOST: ["bad", "This shipment is marked as lost. Please contact the sender or support."],
  RTO_INITIATED: ["warn", "This shipment is being returned to the sender."],
  RTO_IN_TRANSIT: ["warn", "This shipment is on its way back to the sender."],
  RTO_DELIVERED: ["warn", "This shipment was returned to the sender."],
  RETURNED_INTRANSIT: ["warn", "This shipment is being returned to the sender."],
  RECEIVED_AT_RETURN_CENTER: ["warn", "This shipment has reached the return centre."],
  RETURN_OFD: ["warn", "The return is out for delivery to the sender."],
  RETURN_DELIVERED: ["warn", "This shipment was returned to the sender."],
};
const macroStage = (status) => {
  const s = normalizeStatus(status);
  const note = NOTES[s];
  if (HAPPY[s] !== undefined) return { index: HAPPY[s], note: note?.[1], tone: note?.[0] };
  if (s === "NDR" || s === "UNDEL_REATTEMPT") return { index: 3, note: note?.[1], tone: note?.[0] };
  return { index: -1, note: note?.[1] || null, tone: note?.[0] || "warn" }; // exception → no stepper
};

const StatusTracker = ({ index }) => (
  <div className="flex items-start">
    {STAGES.map((s, i) => {
      const done = i <= index;
      const current = i === index;
      return (
        <div key={s.label} className="flex-1 flex flex-col items-center relative min-w-0">
          {i < STAGES.length - 1 && (
            <span className={`absolute top-4 left-1/2 w-full h-0.5 ${i < index ? "bg-brand" : "bg-slate-200"}`} />
          )}
          <span
            className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white ${
              done ? "bg-brand text-white" : "bg-slate-200 text-slate-400"
            } ${current ? "shadow-[0_0_0_4px_rgba(0,166,153,0.18)]" : ""}`}
          >
            <s.icon className="w-4 h-4" />
          </span>
          <span className={`mt-1.5 text-[10px] sm:text-xs text-center leading-tight ${
            current ? "font-semibold text-brand" : done ? "text-slate-600" : "text-slate-400"
          }`}>
            {s.label}
          </span>
        </div>
      );
    })}
  </div>
);

const InfoChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5">
    <Icon className="w-4 h-4 text-brand shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800 break-words">{value}</p>
    </div>
  </div>
);

const TrackPanel = ({ lane }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const q = value.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/${lane.path}`, { params: { [lane.param]: q } });
      setResult(data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) {
        setError("Too many tracking requests. Please wait a minute and try again.");
      } else {
        setError(
          err?.response?.data?.detail ||
            "We couldn't find a shipment for that tracking number. Please check it and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const checkpoints = result ? sortCheckpointsDesc(result.checkpoints) : [];
  const stage = result ? macroStage(result.status) : null;
  const toneCls = { warn: "bg-amber-50 border-amber-200 text-amber-800", bad: "bg-red-50 border-red-200 text-red-700" };

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="space-y-2">
        <label htmlFor={`track-${lane.key}`} className="text-sm font-medium text-slate-700">
          {lane.inputLabel}
        </label>
        <div className="flex flex-col sm:flex-row gap-2.5">
          <Input
            id={`track-${lane.key}`}
            data-testid={`track-input-${lane.key}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={lane.placeholder}
            autoComplete="off"
            className="h-11 text-base flex-1"
            maxLength={40}
          />
          <Button type="submit" className="h-11 sm:w-36" disabled={loading || !value.trim()} data-testid={`track-submit-${lane.key}`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Track</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500">{lane.help}</p>
      </form>

      {error && (
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4" data-testid={`track-error-${lane.key}`}>
          <PackageX className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`track-result-${lane.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 bg-brand-bg/60 px-5 py-4 border-b border-slate-100">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Tracking number</p>
              <p className="font-heading font-bold text-lg text-slate-900 tabular-nums break-all">{result.tracking_number}</p>
              {result.order_number && <p className="text-xs text-slate-400 mt-0.5">Order {result.order_number}</p>}
            </div>
            <StatusBadge status={result.status} kind={lane.kind} size="lg" />
          </div>

          <div className="p-5 space-y-5">
            {stage.index >= 0 && (
              <div className="pt-1 pb-1">
                <StatusTracker index={stage.index} />
              </div>
            )}
            {stage.note && (
              <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${toneCls[stage.tone] || toneCls.warn}`}>
                <PackageX className="w-4 h-4 shrink-0" />
                <span>{stage.note}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {result.courier_name && <InfoChip icon={TruckIcon} label="Courier" value={result.courier_name} />}
              {result.estimated_delivery_date && (
                <InfoChip icon={Clock} label="Estimated delivery" value={fmt(result.estimated_delivery_date)} />
              )}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Tracking history</p>
              {checkpoints.length ? (
                <ol className="relative border-l-2 border-slate-100 pl-6 space-y-5">
                  {checkpoints.map((c, i) => (
                    <li key={i} className="relative">
                      <span
                        className={`absolute -left-[31px] top-0.5 w-3.5 h-3.5 rounded-full ring-4 ring-white ${
                          i === 0 ? "bg-brand" : "bg-slate-300"
                        }`}
                      />
                      <p className={`text-sm ${i === 0 ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                        {c.remark || statusLabel(c.status) || "Update"}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{fmt(c.timestamp)}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-slate-500">
                  No scans yet — tracking will update here as the courier scans your shipment.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edgistify marketing panel (desktop hero / mobile strip) — links to the website.
const EdgistifyPanel = () => (
  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand via-teal-600 to-emerald-600 text-white p-8 shadow-2xl">
    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
    <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
    <div className="relative space-y-6">
      <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider bg-white/15 rounded-full px-3 py-1.5">
        <Sparkles className="w-3.5 h-3.5" /> Powered by Edgistify
      </span>
      <div className="space-y-3">
        <h2 className="text-2xl xl:text-3xl font-heading font-bold leading-tight">
          Real-time visibility on every shipment.
        </h2>
        <p className="text-sm text-white/85 leading-relaxed max-w-sm">
          Edgistify is the AI-native supply chain partner for India's growth-stage brands —
          warehousing, fulfilment and last-mile delivery on one platform.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-sm">
        {EDGISTIFY_STATS.map((s) => (
          <div key={s.label} className="rounded-2xl bg-white/10 backdrop-blur-sm px-4 py-3">
            <p className="text-xl font-heading font-bold tabular-nums">{s.value}</p>
            <p className="text-xs text-white/80">{s.label}</p>
          </div>
        ))}
      </div>
      <Button
        onClick={openEdgistify}
        className="bg-white text-brand hover:bg-white/90 h-11"
        data-testid="edgistify-cta"
      >
        Explore Edgistify <ExternalLink className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </div>
);

const TrackOrderPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50">
      <header className="px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/login")} className="flex items-center gap-3" data-testid="track-brand-home">
          <div className="p-2.5 rounded-xl bg-brand">
            <TruckIcon className="w-7 h-7 text-white" />
          </div>
          <div className="text-left">
            <h1 className="text-xl font-bold text-slate-800 leading-none">EdgeLMS</h1>
            <p className="text-xs font-medium text-slate-500 tracking-wide">by Edgistify</p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={openEdgistify} className="hidden sm:inline-flex text-slate-600" data-testid="track-visit-edgistify">
            Visit Edgistify <ExternalLink className="w-4 h-4 ml-1.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="track-signin">
            <LogIn className="w-4 h-4 mr-2" /> Sign in
          </Button>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center p-4 sm:p-6">
        <div className="w-full max-w-6xl grid lg:grid-cols-[5fr_6fr] items-start gap-8 lg:gap-10 lg:pt-6">
          {/* Marketing panel — full hero on desktop, compact on mobile */}
          <div className="hidden lg:block lg:sticky lg:top-6">
            <EdgistifyPanel />
          </div>

          {/* Tracking column */}
          <div className="w-full max-w-2xl mx-auto lg:mx-0">
            <div className="mb-5 sm:mb-6 text-center lg:text-left space-y-2">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand/10">
                <PackageSearch className="w-6 h-6 text-brand" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Track your shipment</h2>
              <p className="text-sm text-slate-600 max-w-md mx-auto lg:mx-0">
                Enter your tracking number to see live status. No login needed.
              </p>
            </div>

            <Card className="shadow-2xl border-0">
              <CardContent className="pt-6">
                <Tabs defaultValue="b2c" className="w-full">
                  <TabsList className="grid grid-cols-2 w-full mb-5">
                    {Object.values(LANES).map((lane) => (
                      <TabsTrigger key={lane.key} value={lane.key} data-testid={`track-tab-${lane.key}`}>
                        <lane.icon className="w-4 h-4 mr-2" /> {lane.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {Object.values(LANES).map((lane) => (
                    <TabsContent key={lane.key} value={lane.key}>
                      <TrackPanel lane={lane} />
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            <div className="mt-4 flex items-center justify-center lg:justify-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5 text-brand" />
              <span>Status is updated directly from the courier network. We never display personal or payment details here.</span>
            </div>

            {/* Compact Edgistify marketing for phone / tablet portrait */}
            <div className="lg:hidden mt-6 rounded-2xl bg-gradient-to-br from-brand to-emerald-600 text-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-1">Powered by Edgistify</p>
              <p className="text-base font-heading font-bold leading-snug mb-3">AI-native supply chain for India's growth-stage brands.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {EDGISTIFY_STATS.map((s) => (
                  <span key={s.label} className="inline-flex items-baseline gap-1 text-xs bg-white/15 rounded-full px-2.5 py-1">
                    <strong className="tabular-nums">{s.value}</strong> {s.label}
                  </span>
                ))}
              </div>
              <button onClick={openEdgistify} className="inline-flex items-center gap-1.5 text-sm font-semibold underline" data-testid="edgistify-cta-mobile">
                Explore Edgistify <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} OptiSupply Chain Solution Pvt. Ltd. · EdgeLMS ·{" "}
        <button onClick={openEdgistify} className="hover:text-brand underline" data-testid="footer-edgistify">edgistify.com</button>
      </footer>
    </div>
  );
};

export default TrackOrderPage;
